import Vue from 'vue'
import metaInfo from '@/modules/mixins/metaInfo'
import { mapGetters } from 'vuex'
import store from '@/modules/store'
import Spinner from '@/components/atoms/Spinner'
import CartBlock from '@/components/organism/CartBlock'
import Badge from '@/components/molecules/Badge'
import CheckoutPayment from '@/components/organism/checkout/CheckoutPayment'
import CheckoutAuthentication from '@/components/organism/checkout/CheckoutAuthentication'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import InputCheckbox from '@/components/atoms/inputlib/components/InputCheckbox'
import ModalCanteenAddress from '@/components/organism/modal/ModalCanteenAddress'
import DeliveryDetailsBlock from '@/components/organism/location/DeliveryDetailsBlock'
import CheckoutErrorBlock from '@/components/organism/checkout/CheckoutErrorBlock.vue'
import ModalDeliveryStateChange from '@/components/organism/modal/ModalDeliveryStateChange.vue'
import SelectMeal from '@/components/organism/restaurant/SelectMeal'
import ModalConfirm from '@/components/organism/modal/ModalConfirm.vue'
import ModalVatPrompt from '@/components/organism/checkout/ModalVatPrompt.vue'
import { Meal } from '@/api'

import isEmpty from 'lodash.isempty'

const cart = Vue.prototype.$cart
const user = Vue.prototype.$user
const addresses = Vue.prototype.$addresses
const filteredAddresses = Vue.prototype.$filteredAddresses
const meal = Vue.observable(new Meal())

export default {
    mixins: [metaInfo],
    components: {
        CheckoutErrorBlock,
        ModalCanteenAddress,
        InputCheckbox,
        ButtonLoader,
        CheckoutAuthentication,
        CheckoutPayment,
        Badge,
        CartBlock,
        Spinner,
        DeliveryDetailsBlock,
        ModalDeliveryStateChange,
        SelectMeal,
        ModalConfirm,
        ModalVatPrompt,
    },

    data() {
        return {
            loaded: false,
            pageLoading: true,
            confirmCardPayment: false,
        }
    },

    computed: {
        pageTitle() {
            return this.$t('labels.checkout')
        },
        cart: () => cart,
        meal: () => meal,
        ...mapGetters({
            activeCanteen: 'active/canteen',
            currency: 'active/currency',
            checkoutLoading: 'checkout/loading',
            stillLoading: 'checkout/stillLoading',
            stripe: 'checkout/stripe',
            restaurant: 'checkout/restaurant',
            orderErrors: 'checkout/orderErrors',
            orderDisabled: 'checkout/orderDisabled',
            possibleErrors: 'checkout/possibleErrors',
            validInvoiceInfo: 'checkout/validInvoiceInfo',
            calculationErrors: 'checkout/calculationErrors',
            orderAddress: 'order/address',
            expenseRules: 'expenseRules',
            requestInvoiceAvailable: 'site/requestInvoiceAvailable',
        }),
        isLoggedIn() {
            return this.$user.isLoggedIn
        },
        loading() {
            return this.checkoutLoading || this.cart.loading || this.stillLoading
        },
        addresses() {
            return this.$addresses.items
        },
        unavailableAddresses() {
            return this.$addresses.items.filter(
                (address) =>
                    !this.$filteredAddresses.items.some(
                        (filteredAddress) => address.id === filteredAddress.id
                    )
            )
        },
        usableExpenseRules() {
            return (
                this.expenseRules?.filter(
                    (rule) =>
                        !!this.calculation.usable_expense_rules?.find(
                            (usableRules) => usableRules.id === rule.id
                        )
                ) || []
            )
        },
        disabledExpenseRules() {
            return (
                this.expenseRules?.filter(
                    (rule) =>
                        !this.calculation.usable_expense_rules?.find(
                            (usableRules) => usableRules.id === rule.id
                        )
                ) || []
            )
        },
        nextAvailableAllowanceToday() {
            if (this.cart.calculation?.usable_expense_rules?.length) return
            const now = this.$moment()
            return this.expenseRules
                ?.filter((rule) => {
                    if (rule.only_for_company_address) return this.orderAddress.is_company_address
                    else if (rule.only_for_private_address)
                        return !this.orderAddress.is_company_address
                    else return true
                })
                ?.reduce((acc, rule) => {
                    const todaysTimetable = rule.days.find((day) => day.day === now.isoWeekday())
                    if (todaysTimetable) acc.push(todaysTimetable.time_intervals)
                    return acc
                }, [])
                ?.flat()
                ?.filter(({ open }) =>
                    this.$moment(open, 'HH:mm')
                        .set({
                            year: now.year(),
                            month: now.month(),
                            date: now.date(),
                        })
                        .isAfter(now)
                )
                ?.sort((a, b) =>
                    this.$moment(a.open, 'HH:mm').diff(this.$moment(b.open, 'HH:mm'))
                )?.[0]?.open
        },
    },

    watch: {
        'cart.calculation.validation_errors': {
            immediate: true,
            deep: true,
            handler(calculationErrors) {
                this.$store.dispatch('checkout/setCalculationErrors', calculationErrors)
            },
        },
    },

    methods: {
        async openCanteenAddressModal(emit) {
            this.$refs.ModalCanteenAddress.open(emit)
        },
        startOrderingProcess() {
            if (
                this.cart.payment_method &&
                !this.cart.selected_expense_rule &&
                (this.cart.calculation?.usable_expense_rules?.length ||
                    this.nextAvailableAllowanceToday)
            ) {
                this.confirmCardPayment = true
            } else if (
                this.cart.payment_method === this.$cardPayment &&
                !this.cart.selected_expense_rule &&
                this.requestInvoiceAvailable &&
                !this.$refs.checkoutPayment?.needInvoice
            ) {
                this.$refs.ModalVatPrompt.open()
            } else {
                this.placeOrder()
            }
        },
        async placeOrder() {
            try {
                await this.$store.dispatch('checkout/placeOrder')
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Error during placing the order:', e)
            }
        },
        async cancelCardPayment() {
            // has latest expense rule and is selectable
            const latestUsed = this.cart.calculation?.usable_expense_rules?.find(
                (expenseRule) => expenseRule.id === this.cart.calculation?.latest_used_allowance_id
            )
            if (latestUsed) {
                this.cart.selected_expense_rule = latestUsed
                await this.$store.dispatch('setAllowance', latestUsed)
                await this.cart.calculate()
                if (this.cart.calculation.price_sum_user === 0) this.cart.payment_method = null
                this.$scrollTo(`#expenseRule-${latestUsed.id}`)
            } else {
                this.cart.payment_method = null
                this.$nextTick(() => this.$scrollTo('#payment_method'))
            }
        },
        setInvoiceInfoValidity(isValid) {
            this.$store.dispatch('checkout/setInvoiceInfoValidity', isValid)
        },
        onVatNo() {
            this.placeOrder()
        },
        onVatYes() {
            if (this.$refs.checkoutPayment) {
                this.$refs.checkoutPayment.needInvoice = true
            }
            this.$store.dispatch('checkout/setInvoiceInfoValidity', true)
            this.placeOrder()
        },
    },

    async beforeRouteEnter(to, from, next) {
        await store.dispatch('checkout/init')
        let orderRestaurant = store.getters['order/restaurant']
        let orderAddress = store.getters['order/address']
        const restaurant = store.getters['checkout/restaurant']
        const stripe = store.getters['checkout/stripe']
        let slug = orderRestaurant ? orderRestaurant.slug : undefined
        if (slug) {
            if (cart.payment_method === Vue.prototype.$cardPayment) {
                if (cart.selected_card) {
                    Vue.set(stripe, 'selectedCard', cart.selected_card)
                } else if (cart.selected_online_payment) {
                    stripe.selectedOnlinePayment = cart.selected_online_payment
                } else {
                    cart.payment_method = null
                }
            }
            if (user.isLoggedIn && !cart.isCanteen) {
                await addresses.initAddresses
                await filteredAddresses.fetch(orderRestaurant.id)
                if (!orderAddress.id && !isEmpty(orderAddress)) {
                    orderAddress = addresses.findAddress(orderAddress) || orderAddress
                    if (orderAddress) store.dispatch('order/setAddress', orderAddress)
                }
            }

            restaurant
                .fetchRestaurant(slug, {
                    cloudcanteen: cart.cloudcanteen_id,
                    residentialcanteen: cart.residentialcanteen_id,
                })
                .then(async () => {
                    await store.dispatch('active/setRestaurant', restaurant.model)
                    await store.dispatch('active/setCurrency', restaurant.country)
                    Vue.prototype.$restaurant.updateModel(restaurant.model)
                    next(async (vm) => {
                        await cart.calculate()
                        cart.setInvoice()
                        cart.setPhone(user.model, orderAddress)
                        if (Vue.prototype.$user.isLoggedIn) stripe.initOnlinePaymnet()
                        try {
                            await cart.overviewCart()
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.warn('Error during cart overview:', e)
                        } finally {
                            cart.autoPaymentMethod()
                        }
                        vm.loaded = true
                        vm.pageLoading = false
                        vm.$watch('cart.calculation.price_sum_user', function (sum, sumBefore) {
                            if (sumBefore >= 0 && sum !== sumBefore) {
                                this.stripe.updateOnlinePaymnet()
                            }
                        })
                    })
                })
                .catch(() => {
                    next(from)
                })
        } else {
            next(from)
        }
    },

    beforeRouteLeave(to, from, next) {
        filteredAddresses.fetch()
        next()
    },
}
